import 'leaflet/dist/leaflet.css';
import '@module/LinkBox/LinkBox.js';
import '@module/Rating/Rating.js';
import '@sass/citytocity/citytocity.scss';
import { EventDispatcher, createLeafletMap, documentReady, gebi, getSearchBoxConfigFromUrl, getUrlVars, hideElement, isMobileView, setUrlParameter } from '@utils/toolbox';
import { RecentSearches } from '@module/RecentSearches/RecentSearches.js';
import { SearchBox } from '@module/SearchBox/SearchBox';
import { mountPriceGraphs } from '@components/priceGraphs';
import { pricehunter } from '@module/PriceHunter/PriceHunter';
import leaflet from 'leaflet';

const routes = {
	init () {
		this.initSearchBox();
		this.initRouteMetaMap();
		// this.initPricehunter(); // Wait for pricehunter to load before initializing recent searches, this is so we can get the correct height for the recent searches container
		this.initRecentSearches();
		mountPriceGraphs();
	},

	initSearchBox () {
		window.searchBox = new SearchBox(
			{
				id: 'abSearchBox',
				calendarRender: gebi('dateselector'),
				config: getSearchBoxConfigFromUrl(js_params.urlVars),
				callbacks: {
					onPreviewReceived: function (instance, previewGroup) {
						if (previewGroup?.flights?.length) {
							instance.cancelSearch();
							location.href = instance.showresultURL;
						}
					},
					onSearchComplete: (instance) => {
						const urlVars = getUrlVars();

						if (urlVars?.filter) {
							instance.showresultURL = setUrlParameter(instance.showresultURL, 'filter', urlVars.filter);
						}

						location.href = instance.showresultURL;
					}
				}
			});
	},

	async initPricehunter () {
		const pricehunterComponent = await pricehunter.init({
			gestureHandling: isMobileView() ? 'cooperative' : 'greedy',
			zoom: 7,
			center: { ...js_params.toCoordinates }
		}, '#map');

		EventDispatcher.once('pricehunter:refreshed', () => {
			const anyMarkersInView = Object.keys(window.pricehunter_map.showingList).length > 0;

			if (!anyMarkersInView) {
				hideElement('section.container.prisjagaren');
			}
		});

		return pricehunterComponent;
	},

	initRecentSearches () {
		window.recentSearches = new RecentSearches({ recentCityToCity: true, cityFrom: js_params.fromCityIataAndMetro, cityTo: js_params.toCityIataAndMetro });
	},

	initRouteMetaMap () {
		const el = gebi('map__route');

		// Reducing the times that is loaded if it is mobile and is not in view.
		if (isMobileView()) {
			const observer = new IntersectionObserver((entries, obs) => {
				if (entries[0].isIntersecting) {
					obs.disconnect();
					routes.renderRouteMap(el);
				}
			}, { threshold: 0.1 });
			observer.observe(el);
		} else {
			routes.renderRouteMap(el);
		}
	},

	renderRouteMap (el) {
		const { fromCoordinates, toCoordinates } = js_params;
		const from = [fromCoordinates.lat, fromCoordinates.lng];
		const to = [toCoordinates.lat, toCoordinates.lng];

		const map = createLeafletMap(leaflet, el, {
			zoomControl: false,
			dragging: false,
			scrollWheelZoom: false,
			doubleClickZoom: false,
			touchZoom: false,
			keyboard: false,
			attributionControl: true,
			minZoom: 1,
			maxZoom: 10
		});

		// add markers
		const markerIcon = leaflet.icon({
			iconUrl: `/static/v${js_params.version}/assets/images/global/oval.png`,
			iconSize: [24, 24],
			iconAnchor: [12, 12]
		});

		leaflet.marker(from, { icon: markerIcon }).addTo(map);
		leaflet.marker(to, { icon: markerIcon }).addTo(map);

		// add flight line
		leaflet.polyline(geodesicPoints(from, to, 64), {
			color: '#2E5B8B',
			weight: 2,
			opacity: 1
		}).addTo(map);

		map.fitBounds(leaflet.latLngBounds([from, to]), { padding: [40, 40] });
		requestAnimationFrame(() => map.invalidateSize());
	}
};

// Generate the flight line
function geodesicPoints (from, to, steps) {
	const toRad = d => d * Math.PI / 180;
	const toDeg = r => r * 180 / Math.PI;
	const [lat1, lng1] = from.map(toRad);
	const [lat2, lng2] = to.map(toRad);
	const points = [];

	for (let i = 0; i <= steps; i++) {
		const f = i / steps;
		const d = 2 * Math.asin(Math.sqrt(
			Math.sin((lat2 - lat1) / 2) ** 2
			+ Math.cos(lat1) * Math.cos(lat2) * Math.sin((lng2 - lng1) / 2) ** 2
		));

		if (d === 0) {
			points.push(from);
			continue;
		}

		const A = Math.sin((1 - f) * d) / Math.sin(d);
		const B = Math.sin(f * d) / Math.sin(d);
		const x = A * Math.cos(lat1) * Math.cos(lng1) + B * Math.cos(lat2) * Math.cos(lng2);
		const y = A * Math.cos(lat1) * Math.sin(lng1) + B * Math.cos(lat2) * Math.sin(lng2);
		const z = A * Math.sin(lat1) + B * Math.sin(lat2);
		points.push([toDeg(Math.atan2(z, Math.sqrt(x ** 2 + y ** 2))), toDeg(Math.atan2(y, x))]);
	}

	return points;
}

documentReady(routes.init.bind(routes));
